import { Alert, Button, DatePicker, Form, Input, Modal, Popconfirm, Space, TableProps, Tag, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { apiDeleteOutletLink, apiFilterOutletLinks, apiUpdateOutletLink } from '../../api'
import defaultParams from '../../assets/constants/default_params'
import { EXPORT, ORDER } from '../../assets/constants/route_path'
import UserSelect, { loadUserMap } from '../../components/outlet/UserSelect'
import CustomTable from '../../components/table'
import { useLoading } from '../../context/LoadingContext'
import { IOutletInventoryLink } from '../../models/outlet.modal'
import { IUser } from '../../models/user.model'
import { authSelector } from '../../redux/slices/authSlice'
import PermUtils from '../../utils/perm-utils'
import { toastError, toastSuccess } from '../../utils/toast'

const fmtDateTime = (v?: string | null) => {
    if (!v) return '—'
    const d = dayjs(v)
    if (!d.isValid() || d.year() < 2000) return '—'
    return d.format('DD/MM/YYYY HH:mm')
}

const SOURCE_LABELS: Record<string, string> = {
    outlet_order_wrapper: 'Đơn hàng Kho xuất',
    admin_manual: 'Gán thủ công',
    excel_import: 'Import Excel',
}

type Props = {
    outletId: string
    outletName?: string
    cpCode: string
}

export default function TabInventory({ outletId }: Props) {
    const [data, setData] = useState<IOutletInventoryLink[]>([])
    const [total, setTotal] = useState<number>(0)
    const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
    const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
    const [userMap, setUserMap] = useState<Map<number, IUser>>(new Map())
    const [editing, setEditing] = useState<IOutletInventoryLink | null>(null)
    const [form] = Form.useForm()
    const { setIsLoading } = useLoading()
    const authState = useSelector(authSelector)
    const canUpdate = PermUtils.hasAnyAuthority(authState?.account?.listPermission, [PermUtils.canUpdateOutletInventoryLink])
    const canDelete = PermUtils.hasAnyAuthority(authState?.account?.listPermission, [PermUtils.canDeleteOutletInventoryLink])

    useEffect(() => {
        loadUserMap().then(setUserMap)
    }, [])

    const fetchData = useCallback(async () => {
        if (!outletId) return
        try {
            setIsLoading(true)
            const resp: any = await apiFilterOutletLinks({
                outletId,
                limit: pageSize,
                offset: pageSize * (currentPage - 1),
            })
            if (resp?.data == null) {
                setData([])
                setTotal(0)
                return
            }
            setData(resp.data.data ?? [])
            setTotal(Number(resp.data.total ?? 0))
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }, [outletId, currentPage, pageSize, setIsLoading])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const openEdit = (r: IOutletInventoryLink) => {
        setEditing(r)
        form.setFieldsValue({
            linkedAt: r.linkedAt ? dayjs(r.linkedAt) : undefined,
            linkedByUserId: r.linkedByUserId || undefined,
            note: r.note ?? '',
        })
    }

    const handleUpdate = async (values: any) => {
        if (!editing) return
        try {
            setIsLoading(true)
            const resp: any = await apiUpdateOutletLink({
                id: editing.id,
                linkedAt: values.linkedAt ? dayjs(values.linkedAt).toISOString() : undefined,
                linkedByUserId: values.linkedByUserId ? Number(values.linkedByUserId) : undefined,
                note: values.note ?? '',
            })
            if (resp.status === 200) {
                toastSuccess('Cập nhật thành công!')
                setEditing(null)
                form.resetFields()
                fetchData()
                return
            }
            toastError('Có lỗi xảy ra!')
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    const handleDelete = async (id: string) => {
        try {
            setIsLoading(true)
            const resp = await apiDeleteOutletLink(id)
            if (resp.status === 200) {
                toastSuccess('Xoá thành công!')
                fetchData()
                return
            }
            toastError('Có lỗi xảy ra!')
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    const columns: TableProps<IOutletInventoryLink>['columns'] = [
        {
            key: 'STT',
            title: 'STT',
            render: (_, __, index) => ++index + (pageSize * (currentPage - 1)),
            width: 60,
        },
        {
            key: 'linkedAt',
            title: 'Ngày giao',
            dataIndex: 'linkedAt',
            render: v => fmtDateTime(v),
        },
        {
            key: 'simInventoryId',
            title: 'ID Kho SIM',
            dataIndex: 'simInventoryId',
        },
        {
            key: 'orderId',
            title: 'ID Đơn hàng',
            dataIndex: 'orderId',
            render: v => v ? <Tooltip title={v}>…{String(v).slice(-8)}</Tooltip> : '—',
        },
        {
            key: 'range',
            title: 'Serial',
            render: (_, r) => r.startSim && r.endSim ? `${r.startSim} → ${r.endSim}` : '—',
        },
        {
            key: 'totalSim',
            title: 'Số lượng',
            dataIndex: 'totalSim',
            align: 'right',
            render: v => v != null ? v.toLocaleString('vi-VN') : '—',
        },
        {
            key: 'source',
            title: 'Nguồn',
            dataIndex: 'source',
            render: v => v ? <Tag color='geekblue'>{SOURCE_LABELS[v] ?? v}</Tag> : '—',
        },
        {
            key: 'linkedBy',
            title: 'Người giao',
            render: (_, r) => {
                if (!r.linkedByUserId) return '—'
                const u = userMap.get(r.linkedByUserId)
                return u ? (u.full_name || u.username || `#${r.linkedByUserId}`) : `#${r.linkedByUserId}`
            },
        },
        {
            key: 'note',
            title: 'Ghi chú',
            dataIndex: 'note',
        },
        {
            key: 'action',
            title: 'Tác vụ',
            align: 'center',
            width: 140,
            render: (_, r) => (
                <Space size='small'>
                    {canUpdate && <Button size='small' onClick={() => openEdit(r)}>Sửa</Button>}
                    {canDelete && (
                        <Popconfirm
                            title='Xoá bản ghi kho gán đại lý?'
                            okText='Xoá'
                            cancelText='Huỷ'
                            onConfirm={() => handleDelete(r.id)}
                        >
                            <Button danger size='small'>Xoá</Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ]

    return (
        <div>
            <Alert
                type='info'
                showIcon
                style={{ marginBottom: 16 }}
                message={
                    <span>
                        Giao SIM mới cho đại lý: vào <Link to={EXPORT + ORDER}>Kho xuất → Đơn hàng</Link> → nhấn <b>Thêm đơn hàng</b>, chọn công ty của đại lý → chọn đại lý này trong ô "Đại lý (tùy chọn)". Hoặc dùng CLI Excel import (Plan 4) cho bulk.
                    </span>
                }
            />
            <CustomTable
                namePage='lô SIM'
                rowKey='id'
                columns={columns}
                dataSource={data}
                pageSize={pageSize}
                setPageSize={setPageSize}
                total={total}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
            />
            <Modal
                title='Cập nhật lô SIM đại lý'
                open={!!editing}
                footer={null}
                onCancel={() => { setEditing(null); form.resetFields() }}
                destroyOnClose
            >
                <Form form={form} layout='vertical' onFinish={handleUpdate} autoComplete='off'>
                    <Form.Item name='linkedAt' label='Ngày giao'>
                        <DatePicker showTime style={{ width: '100%' }} format='DD/MM/YYYY HH:mm' />
                    </Form.Item>
                    <Form.Item name='linkedByUserId' label='Người giao (Sale)'>
                        <UserSelect placeholder='Chọn sale' />
                    </Form.Item>
                    <Form.Item name='note' label='Ghi chú'>
                        <Input.TextArea rows={4} />
                    </Form.Item>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <Button onClick={() => { setEditing(null); form.resetFields() }}>Huỷ</Button>
                        <Button type='primary' htmlType='submit'>Cập nhật</Button>
                    </div>
                </Form>
            </Modal>
        </div>
    )
}
